import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import SeeItLive from './SeeItLive';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'What is a 13-F filing and who has to file one?',
    a: 'A Form 13-F is a quarterly report filed with the SEC by every institutional investment manager that exercises discretion over at least $100 million of qualifying U.S. equities. It discloses every long equity position held at quarter-end — share count, market value, voting authority, and whether the position is a put or call option. Berkshire Hathaway, Bridgewater, Citadel, Renaissance Technologies, Pershing Square, Scion Asset Management (Michael Burry), and roughly 8,000 other managers file every quarter. The rule was created in 1975 to make institutional ownership transparent to the market, and the filings are the single richest public window into what large asset managers actually own.',
  },
  {
    q: 'Why does 13-F data lag by up to 45 days?',
    a: 'The SEC gives filers 45 days after quarter-end to submit the report. That means the September 30 quarter-end positions are not publicly known until mid-November. A large fund could have completely exited a position weeks before the filing became visible. Treat 13-F data as a slow-moving indicator of positioning trends, not as an actionable buy signal on any single quarter. Institutional turnover in individual positions is generally slower than retail turnover, which is why the lag still contains meaningful information — but a fund that owned a stock at 9/30 may not own it today, and there is no way to know from the filing alone.',
  },
  {
    q: 'What does DiviDrip surface for 13-F data?',
    a: 'Every stock modal has an Insider &amp; Institutional tab that includes a top-200-holders table sourced from the platform\u2019s weekly 13-F bucketing pipeline. Each row shows the filer name (resolved from SEC EDGAR — the raw filings only carry the 10-digit CIK), share count, market value, voting authority breakdown, and a "Smart Money" badge for filers that appear on our curated famous-institutions list (Berkshire, Bridgewater, Citadel, Renaissance, Pershing Square, Scion, Tiger Global, Appaloosa, Icahn, Third Point, Soros, Lone Pine, Viking Global, Elliott, and Two Sigma). A separate Smart-Money Crossover badge appears at the top of the tab whenever three or more famous filers hold the same ticker — a strong consensus signal.',
  },
  {
    q: 'What are the limitations of 13-F data?',
    a: 'Four important gaps. (1) 45-day lag as above. (2) Long-only reporting — short positions are not disclosed on 13-F, so you cannot tell whether a fund holding a stock is net long or partially hedged. (3) Options positions are disclosed but only for common-stock underlyings, and complex derivative structures are invisible. (4) Investment advisors managing separate accounts sometimes file consolidated 13-Fs that mix strategies, so a Bridgewater filing represents dozens of underlying funds with different mandates rather than one coherent portfolio. The Massive endpoint DiviDrip uses gives voting-authority breakdown (sole vs shared) which helps distinguish beneficial ownership from custody-only, but the interpretation still requires judgment.',
  },
  {
    q: 'What is a "Smart Money Crossover" and why does it matter?',
    a: 'A Smart Money Crossover fires when three or more filers from the platform\u2019s hand-curated famous-institutions list hold the same ticker simultaneously. The historical base rate for consensus among top-tier value / event-driven funds is meaningfully above 50% for one-year forward outperformance, especially when the consensus lands during periods of price weakness. It is not a stand-alone buy signal — the same forensic-safety checks from the Hidden Gems vs Value Traps guide still apply — but it is a genuine information signal. Consensus across multiple independent, capital-constrained sophisticated allocators is meaningfully different from a single fund holding a position.',
  },
  {
    q: 'How should retail investors use 13-F data day-to-day?',
    a: 'Three practical applications. (1) Idea generation: scan the Smart-Money Crossover badges across the watchlist to surface consensus names for further work. Never buy on the badge alone — use it as a starting shortlist. (2) Contrarian sizing: if a widely-followed activist (Icahn, Ackman) discloses a new position, retail buying can drive a short-term price spike that fades over 4-8 weeks. Waiting for the initial excitement to settle before entering has historically produced better cost basis. (3) Position confirmation: when you already own a stock and are considering adding, check whether the fund names on the 13-F table have been building or trimming over consecutive quarters. Consistent quarter-over-quarter accumulation is a confirmation. Consistent trimming is not necessarily a sell signal — funds have their own liquidity and mandate constraints — but it deserves an explanation.',
  },
];

export default function LearnInstitutional13FTracking() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Institutional 13-F Tracking — How to Read Hedge-Fund Holdings on DiviDrip | TwylightCrow"
      description="Form 13-F filings show quarter-end holdings for every institutional manager over $100M in U.S. equities. This guide walks through how to read them on DiviDrip — real institutional-holder tables from AAPL, NVDA, AMZN, META with FMR, Geode, Wellington, CalSTRS, and the Smart-Money Crossover badge for consensus among famous funds (Berkshire, Bridgewater, Citadel, Scion, Pershing Square)."
      canonical="https://twylightcrow.com/learn/institutional-13f-tracking"
      guidePath="/learn/institutional-13f-tracking"
      sectionLabel="Capital Allocation &amp; Tax"
      sectionAnchor="non-div-capital-allocation"
      kicker="Learn · Non-Dividend Stocks"
      h1="Institutional 13-F Tracking — Reading Smart-Money Holdings"
      datePublished="2026-07-04"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/founders-vision-execution', label: 'Founder\'s Vision — the management side of the ownership story' },
        { to: '/learn/hidden-gems-vs-value-traps', label: 'Hidden Gems vs Value Traps — the forensic gate before acting on any signal' },
        { to: '/learn/valuation-non-dividend-stocks', label: 'Valuing Non-Dividend Stocks — the multiples that inform smart-money entries' },
        { to: '/glossary#entry-institutional-holders-top-13f', label: 'Glossary: Institutional Holders (Top 13-F)' },
      ]}
    >
      <p>
        Every quarter, the largest investment managers in the world
        publicly disclose every long equity position they hold. The
        rule was written in 1975 to make institutional ownership
        transparent to the market, and it produces the single richest
        window into what capital-constrained sophisticated allocators
        actually own. Berkshire Hathaway files. Bridgewater files.
        Citadel, Renaissance, Pershing Square, Scion (Michael Burry),
        Tiger Global, Appaloosa, and roughly 8,000 other managers
        with at least $100M in U.S. equities file too.
      </p>
      <p>
        DiviDrip by TwylightCrow ingests every 13-F filing weekly via
        the Massive API and buckets the holdings by ticker into a
        top-200 institutional-holders table on every stock modal.
        This guide walks through how the data lands in the app, how
        to read it, what its limitations are, and how the platform&rsquo;s
        Smart-Money Crossover badge turns raw filings into a
        surfacable consensus signal.
      </p>

      <h2>How the pipeline works</h2>
      <ol>
        <li>
          <strong>Weekly ingest.</strong> A cron job (7-day cooldown)
          pulls the full 13-F rowset from Massive&rsquo;s
          <code>/stocks/filings/vX/13-F</code> endpoint &mdash; roughly
          300,000+ rows per pass covering the last several quarters.
        </li>
        <li>
          <strong>CUSIP → ticker resolution.</strong> Each 13-F row
          identifies the security by CUSIP, not by ticker. A cached
          CUSIP-to-ticker map (currently ~9,900 entries and growing
          via an OpenFIGI-backed backfill) resolves the CUSIP to the
          exchange-listed ticker.
        </li>
        <li>
          <strong>Filer name resolution.</strong> Raw 13-F filings
          only carry the 10-digit filer CIK. A separate SEC EDGAR
          resolver looks up the real entity name (e.g. &ldquo;GEODE
          CAPITAL MANAGEMENT, LLC&rdquo; instead of &ldquo;0001214717&rdquo;),
          cached for 90 days.
        </li>
        <li>
          <strong>Per-ticker bucketing.</strong> Rows are grouped by
          ticker, sorted by market value, and the top 200 holders
          are written to the <code>thirteen_f_by_ticker</code>
          collection. This is what powers the Insider &amp;
          Institutional tab table.
        </li>
        <li>
          <strong>Famous-institution flagging.</strong> Every row is
          checked against a hand-curated allow-list (Berkshire,
          Bridgewater, Citadel, Renaissance, Two Sigma, Pershing
          Square, Tiger Global, Appaloosa, Icahn, Fairholme, Scion,
          Third Point, Soros, Lone Pine, Viking Global, Elliott).
          When three or more famous filers hold the same ticker, a
          Smart-Money Crossover badge fires at the top of the tab.
        </li>
      </ol>

      <h2>What the top-institutional-holders table looks like</h2>
      <p>
        A few live examples pulled directly from the platform this
        week. Numbers are quarter-end market values rounded to the
        nearest $1M. The full table on the app shows share count,
        voting authority breakdown, and per-holder trend
        indicators.
      </p>

      <h3>Apple (AAPL) &mdash; the megacap consensus name</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Filer</th>
            <th>Market Value</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">GEODE CAPITAL MANAGEMENT, LLC</td>
            <td className="font-mono">$81.9B</td>
            <td>Fidelity&rsquo;s index-fund sub-advisor. Largest single 13-F position by MV.</td>
          </tr>
          <tr>
            <td className="font-semibold">FMR LLC (Fidelity)</td>
            <td className="font-mono">$49.3B</td>
            <td>Active-fund manager. Combined with Geode above, Fidelity&rsquo;s ecosystem holds over $131B of AAPL.</td>
          </tr>
          <tr>
            <td className="font-semibold">BANK OF AMERICA CORP</td>
            <td className="font-mono">$8.5B</td>
            <td>Trust and wealth-management custody.</td>
          </tr>
          <tr>
            <td className="font-semibold">H&amp;H International Investment</td>
            <td className="font-mono">$7.3B</td>
            <td>Family-office aggregator.</td>
          </tr>
          <tr>
            <td className="font-semibold">CALIFORNIA STATE TEACHERS RETIREMENT SYS</td>
            <td className="font-mono">$5.7B</td>
            <td>CalSTRS. Long-only pension. Consistent multi-year holder.</td>
          </tr>
        </tbody>
      </table>
      <p>
        <SeeItLive ticker="AAPL" tab="insider-activity" tone="violet" label="See AAPL 13-F live &rarr;" />
      </p>

      <h3>Nvidia (NVDA) &mdash; the AI-cycle consensus name</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Filer</th>
            <th>Market Value</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">FMR LLC (Fidelity)</td>
            <td className="font-mono">$120.8B</td>
            <td>Largest concentrated position across Fidelity&rsquo;s active books &mdash; a very large signal on active-manager conviction.</td>
          </tr>
          <tr>
            <td className="font-semibold">JANUS HENDERSON GROUP PLC</td>
            <td className="font-mono">$12.4B</td>
            <td>Growth-focused active manager.</td>
          </tr>
          <tr>
            <td className="font-semibold">AMERIPRISE FINANCIAL INC</td>
            <td className="font-mono">$11.2B</td>
            <td>Wealth-management advisor aggregator.</td>
          </tr>
          <tr>
            <td className="font-semibold">BANK OF AMERICA CORP</td>
            <td className="font-mono">$7.2B</td>
            <td>Trust and wealth custody.</td>
          </tr>
          <tr>
            <td className="font-semibold">CALIFORNIA STATE TEACHERS RETIREMENT SYS</td>
            <td className="font-mono">$6.4B</td>
            <td>Pension long-only holder.</td>
          </tr>
        </tbody>
      </table>
      <p>
        <SeeItLive ticker="NVDA" tab="insider-activity" tone="violet" label="See NVDA 13-F live &rarr;" />
      </p>

      <h3>Meta (META) &mdash; the growth-manager consensus name</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Filer</th>
            <th>Market Value</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">GEODE CAPITAL MANAGEMENT, LLC</td>
            <td className="font-mono">$27.0B</td>
            <td>Index-fund exposure. Top megacap holding across the Fidelity family.</td>
          </tr>
          <tr>
            <td className="font-semibold">WELLINGTON MANAGEMENT GROUP LLP</td>
            <td className="font-mono">$5.9B</td>
            <td>Institutional active manager, generally growth-oriented on this name.</td>
          </tr>
          <tr>
            <td className="font-semibold">AMERIPRISE FINANCIAL INC</td>
            <td className="font-mono">$3.3B</td>
            <td>Wealth-management aggregator.</td>
          </tr>
          <tr>
            <td className="font-semibold">AMERICAN CENTURY COMPANIES INC</td>
            <td className="font-mono">$2.9B</td>
            <td>Kansas-based active-value shop.</td>
          </tr>
          <tr>
            <td className="font-semibold">BANK OF AMERICA CORP</td>
            <td className="font-mono">$2.7B</td>
            <td>Trust custody.</td>
          </tr>
        </tbody>
      </table>
      <p>
        <SeeItLive ticker="META" tab="insider-activity" tone="violet" label="See META 13-F live &rarr;" />
      </p>

      <h2>What the Smart-Money Crossover badge means</h2>
      <p>
        The famous-institutions allow-list contains 16 filers we
        treat as elevated signal:
      </p>
      <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm">
        <li>Berkshire (Buffett)</li>
        <li>Bridgewater (Dalio)</li>
        <li>Citadel (Griffin)</li>
        <li>Renaissance Tech</li>
        <li>Two Sigma</li>
        <li>Pershing Square (Ackman)</li>
        <li>Tiger Global</li>
        <li>Appaloosa (Tepper)</li>
        <li>Icahn Enterprises</li>
        <li>Fairholme (Berkowitz)</li>
        <li>Scion (Burry)</li>
        <li>Third Point (Loeb)</li>
        <li>Soros Fund Management</li>
        <li>Lone Pine Capital</li>
        <li>Viking Global</li>
        <li>Elliott Management</li>
      </ul>
      <p className="mt-4">
        When three or more of these show up simultaneously in the
        same stock&rsquo;s 13-F table, the Smart-Money Crossover
        badge fires. It is a genuine information signal &mdash;
        consensus across independent, capital-constrained,
        sophisticated allocators is different in kind from a single
        fund holding the position. It is not a stand-alone buy
        signal, because these managers each have their own risk
        tolerance, holding period, and mandate. Use it as an
        idea-generation lens and then run the forensic-safety and
        valuation checks separately.
      </p>

      <h2>Historical case studies</h2>

      <h3>Michael Burry vs the housing bubble (2005-2008)</h3>
      <p>
        Michael Burry, founder of Scion Asset Management, publicly
        disclosed via his 13-F filings and letters to LPs an
        increasingly large short position on subprime mortgage
        derivatives beginning in 2005. Retail readers who followed
        the disclosures had a two-to-three year window to reduce
        their own housing-linked exposure before the 2008
        financial crisis. Not everyone would have interpreted the
        disclosures correctly, but the raw information was
        publicly available.
      </p>

      <h3>Berkshire&rsquo;s Apple entry (2016-2018)</h3>
      <p>
        Berkshire Hathaway&rsquo;s first 13-F disclosure of an
        Apple position came in May 2016 for the Q1 2016 quarter,
        showing 9.8M shares. By the end of 2018 the position had
        grown to over 250M shares, at which point it was
        Berkshire&rsquo;s largest single equity position at
        roughly $40B. Retail investors who bought Apple alongside
        Berkshire&rsquo;s accumulation timeline in 2016-2018
        earned roughly a 3x return on the stock through 2024
        before Berkshire began trimming in 2024-2025. The 13-F
        trail was the earliest public evidence of Berkshire&rsquo;s
        pivot toward high-quality technology.
      </p>

      <h3>Bill Ackman&rsquo;s Chipotle stake (2016)</h3>
      <p>
        Pershing Square disclosed a 9.9% stake in Chipotle
        Mexican Grill in September 2016, shortly after the
        company&rsquo;s E. coli crisis. The disclosure lifted the
        stock roughly 6% on the day, and the position eventually
        contributed to Ackman&rsquo;s activist push for board
        changes and a share-buyback acceleration. Between the
        13-F disclosure timeline and Ackman&rsquo;s eventual sale
        in 2024, Chipotle roughly 6x&rsquo;d. Retail investors
        who acted on the initial 13-F disclosure earned a very
        strong return, though the wait was long.
      </p>

      <h3>The failed activist case &mdash; Ackman on Valeant (2015-2016)</h3>
      <p>
        Not every famous-manager position works. In 2015 Pershing
        Square disclosed a large position in Valeant
        Pharmaceuticals near $200 per share. The stock then
        collapsed over 90% amid accounting and pricing scandals,
        and Ackman ultimately exited at a massive loss in early
        2017. Retail investors who bought alongside the
        disclosure lost the majority of their capital. The
        lesson: even the highest-conviction Smart Money can be
        wrong. Never abandon the forensic-safety check just
        because a famous name is on the buyer list. The Beneish
        M-Score on Valeant was tripping the manipulation flag
        during Ackman&rsquo;s accumulation period &mdash; a
        retail investor running the forensic check alongside the
        13-F signal would have avoided the trade.
      </p>

      <h2>The 3-step 13-F workflow</h2>
      <ol>
        <li>
          <strong>Open the stock modal, Insider &amp; Institutional
          tab.</strong> Read the Smart-Money Crossover badge first
          (if present) &mdash; that tells you how many famous
          filers hold the name simultaneously.
        </li>
        <li>
          <strong>Scan the top-20 holders.</strong> Note the mix.
          A stock dominated by index-fund holders (Vanguard, Geode,
          BlackRock, State Street) tells you nothing about active
          conviction &mdash; the passive money holds because the
          stock is in the S&amp;P 500, not because it&rsquo;s a
          good investment. A stock with heavy weighting toward
          active managers (Wellington, Janus, Fidelity active,
          T. Rowe Price) tells you active-manager conviction is
          real.
        </li>
        <li>
          <strong>Cross-reference against the forensic gate.</strong>{' '}
          Never buy a name because a famous fund holds it without
          running the Capital Analytics forensic-safety check.
          The Valeant example above shows why. Sophisticated
          allocators are wrong regularly. The base rate of being
          wrong is high enough that piggy-backing without your
          own analysis is a losing strategy in the long run.
        </li>
      </ol>

      <h2>Coverage caveats</h2>
      <p>
        Roughly 55% of the U.S. equity universe currently has full
        13-F bucketing on the platform, with the remainder either
        (a) too small to attract $100M+ institutional filers, or
        (b) blocked by CUSIP resolution gaps that are being
        closed via an ongoing OpenFIGI backfill. Coverage improves
        weekly as the bucketing cron runs. If a specific ticker
        shows the &ldquo;coming soon&rdquo; fallback in the app, that is
        the CUSIP-resolution gap talking &mdash; the underlying
        13-F data exists but has not yet been mapped to a
        ticker.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open any large-cap name on the{' '}
        <Link to="/dashboard">Dashboard</Link> and switch to the
        Insider &amp; Institutional tab. Scan the top-20
        holders. Note whether the mix is index-heavy or
        active-heavy. Look for the Smart-Money Crossover badge
        at the top. If it fires, run the Capital Analytics
        forensic-safety check before treating the consensus as
        actionable. The signal is real but never sufficient on
        its own.
      </p>

      <p className="mt-6">
        For the underlying data mechanics, see the{' '}
        <AbbrTip term="INSTITUTIONAL_HOLDERS">Institutional Holders (Top 13-F)
        glossary entry</AbbrTip>. For the forensic gate that turns
        13-F consensus into a defensible entry, see the{' '}
        <Link to="/learn/hidden-gems-vs-value-traps">
          Hidden Gems vs Value Traps
        </Link>{' '}
        guide.
      </p>
      <p className="text-xs text-muted-foreground italic">
        This guide is educational. 13-F filings lag by up to 45
        days and disclose only long positions. Institutional
        holdings are not investment advice. Historical
        performance is not a forecast.
      </p>
    </LearnPage>
  );
}
